import type { ParameterValueSaveData } from "@/composables/collectionItem/params";
import { useParametersApi } from "@/composables/collectionItem/params";
import type { AiIdentification, AiIdentification_LegoMinifigure, AiIdentificationSummary } from "@/services/aiIdentification/aiIdentificationTypes";
import { CategoryId } from "@/services/collection/categories";
import type { CollectionItem, CreateCollectionItemData } from "@/services/collection/item";
import { createCollectionItem, updateCollectionItem } from "@/services/collection/item";
import { setImagesFromAiIdentification } from "@/services/collection/item/gallery";
import * as PostHog from "@/services/analytics/postHog";
import { factoryImage, type Image } from "@/services/images";
import type { PriceNumeric } from "@/services/prices";
import { isNumericPrice } from "@/services/prices";
import type { Parameters } from "@/services/collection/parameters";
import { addIntoCollection as createCollectionItemFromCollectible } from "@/services/collectibles";
import type { CollectibleElastic } from "@/services/collectibles/elastic";

export const createCollectionItemFromIdentification = async (identification: AiIdentification): Promise<CollectionItem> => {
  const { saveSelectedValue: saveParameterValue } = useParametersApi();

  let item: CollectionItem;
  const promises = [];

  // 1) Create item itself
  if (isCollectibleBasedIdentification(identification)) {
    const response = await createCollectionItemFromCollectible(identification.collectible, false);
    if (response) {
      item = response;
    } else {
      throw new Error("Failed to create collection item from collectible");
    }
  } else {
    const categoryId = identification.categoryId;
    let creationData: CreateCollectionItemData;
    if (categoryId === CategoryId.LegoSets || categoryId === CategoryId.LegoMinifigures) {
      creationData = {
        categoryId,
        collectibleId: identification.catalogueItem.id,
        set: identification.catalogueItem.id,
        minifigure: identification.catalogueItem.id,
        userCollectionListName: "",
      };
    } else {
      creationData = {
        categoryId,
        customName: identification.name,
        userCollectionListName: "",
      };
    }
    item = await createCollectionItem(creationData);

    // 2) Add parameters
    identification.parameters.map((param) => {
      const saveData: ParameterValueSaveData = {
        itemId: item.id,
        parameterId: param.parameterId,
        value: param.valueId,
        type: "predefined",
      };
      promises.push(saveParameterValue(saveData));
    });
  }

  // 3) Add images
  promises.push(setImagesFromAiIdentification(item, identification));

  // 5) Set identification origin
  const aiIdentificationSourceId = identification.id;
  promises.push(updateCollectionItem(item.id, { aiIdentificationSourceId }));

  // 6) Save AI-generated description
  const publicNote = identification.description ?? "";
  promises.push(updateCollectionItem(item.id, { publicNote }));

  await Promise.all(promises);
  PostHog.trackEvent(PostHog.EventName.AiIdentification_ItemCreated);
  return item;
};

export const SupportedCategoryIds = [CategoryId.SportsCards, CategoryId.Pokemon];

/** @todo rework into composable?? */
export const getCoverImage = (item: AiIdentificationSummary): Image | undefined => {
  const img = item.images.length > 0 ? item.images[0] : undefined;
  if (img) {
    return factoryImage(0 /* fake ID */, img.imageUrl, item.name);
  } else {
    return undefined;
  }
};

export const getImages = (item: AiIdentificationSummary | AiIdentification): Image[] => item.images.map(({ imageUrl }) => factoryImage(0 /* fake ID */, imageUrl, item.name));

export const getParamValue = (param: Parameters.Parameter, item: AiIdentification): string | undefined => item.parameters.find((p) => p.parameterId === param.id)?.value;

export const hasPrice = (item: AiIdentificationSummary | AiIdentification): item is (AiIdentification | AiIdentification) & { priceFrom: PriceNumeric; priceTo: PriceNumeric } => isNumericPrice(item.priceFrom) && isNumericPrice(item.priceTo);

export const isLegoMinifigIdentification = (item: AiIdentification): item is AiIdentification_LegoMinifigure => item.categoryId === CategoryId.LegoMinifigures;
export const isCollectibleBasedIdentification = (item: AiIdentification): item is AiIdentification & { collectible: CollectibleElastic } => item.categoryId === CategoryId.Pokemon && item.collectible !== null;
